import { PROMOTION_TYPES } from '@/data/constants/promoIconConstants';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useSettings } from '@/data/Settings';
import { ProductType, ResponseProductAttribute } from '@/data/types/Product';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { PROMO_LIST } from '@/data/utils/usePromoIcons/promoLists';
import { validateStoreExclude } from '@/data/utils/validateStoreExclude';
import { SxProps, Theme } from '@mui/material';
import { useCallback, useMemo } from 'react';

export type PromotionIconStyleType = {
	icon: string;
	iconPlp?: string;
	styles: SxProps<Theme> | undefined;
	badgesStyles: SxProps<Theme> | undefined;
	badgesStylesPlp?: SxProps<Theme>;
	identifier: string;
};

export const getPromotions = ({
	productAttributes,
	marketId,
	physicalStoreId,
	cdnUrl,
}: {
	productAttributes: ProductType['attributes'];
	marketId: string;
	physicalStoreId: string;
	cdnUrl: string;
}) => {
	const attributes =
		productAttributes
			?.map((attribute: ResponseProductAttribute) => {
				let validAttribute = true;
				if (
					attribute.identifier === 'PRO_MARKET_BUYABLE_EXCLUDE' ||
					attribute.identifier === 'MARKET_BUYABLE_EXCLUDE' ||
					attribute.identifier === 'PRO_STORE_ONLY_AVAILABLE' ||
					attribute.identifier === 'STORE_ONLY_AVAILABLE'
				) {
					validAttribute = validateStoreExclude(attribute, marketId);
				}

				if (
					attribute.identifier === 'PRO_STORE_BUYABLE_EXCLUDE' ||
					attribute.identifier === 'STORE_BUYABLE_EXCLUDE'
				) {
					validAttribute = validateStoreExclude(attribute, physicalStoreId);
				}

				if (validAttribute) return attribute.identifier;
				return;
			})
			.filter((attr) => typeof attr === 'string') ?? [];

	const rawPromos = PROMO_LIST.filter((obj) => {
		const [k] = Object.keys(obj);
		return attributes?.includes(k);
	});

	const promotions = rawPromos
		.map<PromotionIconStyleType | undefined>((promo) => {
			const identifier = Object.keys(promo)[0];
			const value = Object.values(promo)[0];

			if (!value?.iconUrl) return;

			return {
				icon: cdnUrl + value.iconUrl,
				iconPlp: value.iconUrlPlp ? cdnUrl + value.iconUrlPlp : undefined,
				styles: value.styles,
				badgesStyles: value.badgesStyles,
				badgesStylesPlp: value.badgesStylesPlp,
				identifier,
			};
		})
		.filter((item): item is PromotionIconStyleType => !!item);

	return promotions;
};

export const usePromoIconsWithStyleAndUrls = ({
	product,
}: {
	product: ProductType | undefined;
}) => {
	const { settings } = useSettings();
	const { physicalStoreId } = useDefaultStore();
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const cdnUrl =
		settings.userData.IMAGE_SERVER_HTTPS_HOSTNAME || settings.userData.IMAGE_SERVER_HOSTNAME || '';

	return useMemo(() => {
		if (!product?.attributes) {
			return {
				promotion: undefined,
				promotions: [],
			};
		}

		const promotions = getPromotions({
			productAttributes: product.attributes,
			marketId,
			physicalStoreId,
			cdnUrl,
		});

		return {
			promotion: promotions.length ? promotions[0] : undefined,
			promotions,
		};
	}, [cdnUrl, marketId, physicalStoreId, product?.attributes]);
};

export const hasFreeGiftPromoIcon = ({
	productAttributes,
	marketId,
	physicalStoreId,
}: {
	productAttributes: ProductType['attributes'];
	marketId: string;
	physicalStoreId: string;
}): boolean =>
	getPromotions({ productAttributes, marketId, physicalStoreId, cdnUrl: '' }).some(
		(promo) => promo.identifier === PROMOTION_TYPES.PROMOICON_FP
	);

export const useHasFreeGiftPromoIconPredicate = () => {
	const { physicalStoreId } = useDefaultStore();
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);

	return useCallback(
		(product: ProductType | undefined) =>
			product?.attributes
				? hasFreeGiftPromoIcon({ productAttributes: product.attributes, marketId, physicalStoreId })
				: false,
		[marketId, physicalStoreId]
	);
};

export const useHasFreeGiftPromoIcon = (product: ProductType | undefined): boolean => {
	const hasFreeGiftIcon = useHasFreeGiftPromoIconPredicate();
	return useMemo(() => hasFreeGiftIcon(product), [hasFreeGiftIcon, product]);
};
